import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useCan, useCurrency } from '@/context/SettingsContext';
import axios from 'axios';
import { 
  Receipt,
  Clock,
  Eye,
  Check,
  X,
  Printer,
  MagnifyingGlass,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

export const OrdersPage = () => {
  const { currentOutlet, orders, fetchOrders, updateOrderStatus, processPayment } = useApp();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const canCancel = useCan('orders.cancel');
  const canEdit = useCan('orders.edit');
  const sym = useCurrency();

  // Cancel / Refund dialog state
  const [actionDialog, setActionDialog] = useState(null);
  // { mode: 'cancel' | 'refund', order, selected: Set<number>, reason: '', amount: '' }

  const openAction = (mode, order) => {
    setActionDialog({
      mode,
      order,
      selected: new Set(),
      reason: '',
      amount: '',
    });
  };

  const toggleItem = (idx) => {
    setActionDialog((d) => {
      const s = new Set(d.selected);
      if (s.has(idx)) s.delete(idx); else s.add(idx);
      return { ...d, selected: s };
    });
  };

  const computeLineTotal = (it) =>
    ((it.price ?? it.base_price ?? 0) + (it.modifier_total || 0)) * (it.quantity || 1);

  const submitAction = async () => {
    if (!actionDialog) return;
    const { mode, order, selected, reason, amount } = actionDialog;
    const isPartial = selected.size > 0;
    try {
      if (mode === 'cancel') {
        await axios.post(
          `${API_URL}/api/orders/${order.id}/cancel`,
          {
            reason,
            item_indices: isPartial ? Array.from(selected) : null,
          },
          { withCredentials: true },
        );
        toast.success(isPartial ? 'Items cancelled' : 'Order cancelled');
      } else {
        const body = { reason };
        if (isPartial) body.item_indices = Array.from(selected);
        else if (amount) body.amount = parseFloat(amount);
        await axios.post(`${API_URL}/api/orders/${order.id}/refund`, body, { withCredentials: true });
        toast.success('Refund processed');
      }
      setActionDialog(null);
      setShowOrderDetail(false);
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${mode} order`);
    }
  };

  useEffect(() => {
    if (currentOutlet) {
      fetchOrders();
      const interval = setInterval(fetchOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [currentOutlet, fetchOrders]);

  const filteredOrders = orders.filter(order => {
    let statusMatch = true;
    if (statusFilter === 'active') {
      statusMatch = ['new', 'confirmed', 'preparing', 'ready'].includes(order.status);
    } else if (statusFilter !== 'all') {
      statusMatch = order.status === statusFilter;
    }
    
    const searchMatch = searchQuery 
      ? order.order_number.includes(searchQuery) || 
        order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    
    return statusMatch && searchMatch;
  });

  const getStatusBadge = (status) => {
    const classes = {
      new: 'status-new',
      confirmed: 'status-preparing',
      preparing: 'status-preparing',
      ready: 'status-ready',
      served: 'status-ready',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return `status-badge ${classes[status] || 'status-badge'}`;
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setLoading(true);
    try {
      await updateOrderStatus(orderId, newStatus);
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (e) {
      console.error('Error updating status:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (order) => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${order.id}`, { withCredentials: true });
      setSelectedOrder(response.data);
      setShowOrderDetail(true);
    } catch (e) {
      console.error('Error fetching order details:', e);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to view orders</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Orders</h1>
          <p className="text-muted-foreground mt-1">{currentOutlet?.name}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by order # or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="order-search"
          />
        </div>
        
        <div className="flex gap-2">
          {['active', 'completed', 'cancelled', 'all'].map((filter) => (
            <button
              key={filter}
              onClick={() => setStatusFilter(filter)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === filter 
                  ? 'bg-primary text-white' 
                  : 'bg-surface hover:bg-secondary'
              }`}
              data-testid={`filter-${filter}`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Order #</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Items</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Total</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Time</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-surface transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-primary">#{order.token_number}</span>
                      <span className="text-xs text-muted-foreground">{order.order_number}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-sm">{order.order_type.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-sm">{order.customer_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{order.items?.length || 0} items</td>
                  <td className="px-4 py-3 font-medium">{sym}{order.total?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={getStatusBadge(order.status)}>{order.status}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatTime(order.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewOrder(order)}
                        data-testid={`view-order-${order.id}`}
                        title="View details"
                      >
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                      {order.status === 'new' && canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStatusChange(order.id, 'confirmed')}
                          className="text-accent-green"
                          data-testid={`confirm-order-${order.id}`}
                          title="Confirm order"
                        >
                          <Check size={16} className="mr-1" /> Confirm
                        </Button>
                      )}
                      {['new', 'confirmed', 'preparing', 'ready'].includes(order.status)
                        && order.payment_status !== 'paid' && canCancel && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAction('cancel', order)}
                          className="text-red-500 hover:bg-red-50"
                          data-testid={`cancel-order-${order.id}`}
                          title="Cancel order"
                        >
                          <X size={16} className="mr-1" /> Cancel
                        </Button>
                      )}
                      {order.payment_status === 'paid' && canCancel && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAction('refund', order)}
                          className="text-amber-600 hover:bg-amber-50"
                          data-testid={`refund-order-${order.id}`}
                          title="Refund"
                        >
                          <ArrowCounterClockwise size={16} className="mr-1" /> Refund
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Receipt size={24} />
              Order #{selectedOrder?.token_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <ScrollArea className="flex-1">
              <div className="space-y-6 py-4">
                {/* Order Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Order Number:</span>
                    <span className="ml-2 font-medium">{selectedOrder.order_number}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 font-medium capitalize">{selectedOrder.order_type.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className={`ml-2 ${getStatusBadge(selectedOrder.status)}`}>{selectedOrder.status}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Payment:</span>
                    <span className={`ml-2 status-badge ${
                      selectedOrder.payment_status === 'paid' ? 'status-ready' :
                      selectedOrder.payment_status === 'partial' ? 'status-new' :
                      'status-cancelled'
                    }`}>{selectedOrder.payment_status}</span>
                  </div>
                  {selectedOrder.customer_name && (
                    <div>
                      <span className="text-muted-foreground">Customer:</span>
                      <span className="ml-2 font-medium">{selectedOrder.customer_name}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <span className="ml-2">{new Date(selectedOrder.created_at).toLocaleString()}</span>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="font-heading font-bold text-lg mb-3">Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="bg-surface rounded-lg p-3">
                        <div className="flex justify-between">
                          <div>
                            <span className="font-bold">{item.quantity}x</span>
                            <span className="ml-2 font-medium">{item.menu_item_name}</span>
                          </div>
                          <span className="font-medium">${item.item_total?.toFixed(2)}</span>
                        </div>
                        {item.modifiers?.length > 0 && (
                          <div className="mt-1 ml-6 space-y-0.5">
                            {item.modifiers.map((mod, i) => (
                              <p key={i} className="text-sm text-primary">
                                + {mod.option_name} {mod.price > 0 && `($${mod.price.toFixed(2)})`}
                              </p>
                            ))}
                          </div>
                        )}
                        {item.notes && (
                          <p className="mt-1 ml-6 text-sm text-accent-red">Note: {item.notes}</p>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          <span className={`status-badge ${
                            item.status === 'ready' ? 'status-ready' :
                            item.status === 'preparing' ? 'status-preparing' :
                            item.status === 'served' ? 'status-completed' :
                            'status-new'
                          }`}>{item.status}</span>
                          <span className="text-xs text-muted-foreground capitalize">Station: {item.kitchen_station}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span>${selectedOrder.tax?.toFixed(2)}</span>
                  </div>
                  {selectedOrder.service_charge > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Service Charge</span>
                      <span>${selectedOrder.service_charge?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                    <span>Total</span>
                    <span>${selectedOrder.total?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-border">
                  {['new', 'confirmed', 'preparing', 'ready'].includes(selectedOrder.status) && (
                    <>
                      {selectedOrder.status === 'new' && (
                        <Button
                          onClick={() => handleStatusChange(selectedOrder.id, 'confirmed')}
                          className="flex-1 bg-primary hover:bg-primary-hover text-white"
                          disabled={loading}
                        >
                          Confirm Order
                        </Button>
                      )}
                      {selectedOrder.status === 'confirmed' && (
                        <Button
                          onClick={() => handleStatusChange(selectedOrder.id, 'preparing')}
                          className="flex-1 bg-accent-yellow hover:bg-yellow-500 text-black"
                          disabled={loading}
                        >
                          Start Preparing
                        </Button>
                      )}
                      {selectedOrder.status === 'preparing' && (
                        <Button
                          onClick={() => handleStatusChange(selectedOrder.id, 'ready')}
                          className="flex-1 bg-accent-green hover:bg-green-600 text-white"
                          disabled={loading}
                        >
                          Mark Ready
                        </Button>
                      )}
                      {selectedOrder.status === 'ready' && (
                        <Button
                          onClick={() => handleStatusChange(selectedOrder.id, 'completed')}
                          className="flex-1 bg-accent-green hover:bg-green-600 text-white"
                          disabled={loading}
                        >
                          Complete Order
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => openAction('cancel', selectedOrder)}
                        className="text-accent-red border-accent-red hover:bg-accent-red/10"
                        disabled={loading || selectedOrder.payment_status === 'paid'}
                        data-testid="detail-cancel-btn"
                      >
                        <X size={14} className="mr-1" /> Cancel
                      </Button>
                    </>
                  )}
                  {selectedOrder.payment_status === 'paid' && (
                    <Button
                      variant="outline"
                      onClick={() => openAction('refund', selectedOrder)}
                      className="text-amber-600 border-amber-500 hover:bg-amber-50"
                      data-testid="detail-refund-btn"
                    >
                      <ArrowCounterClockwise size={14} className="mr-1" /> Refund
                    </Button>
                  )}
                  <Button variant="outline">
                    <Printer size={16} className="mr-2" />
                    Print Receipt
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Cancel / Refund dialog ===== */}
      <Dialog open={!!actionDialog} onOpenChange={(open) => !open && setActionDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="cancel-refund-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {actionDialog?.mode} order #{actionDialog?.order?.token_number}
            </DialogTitle>
          </DialogHeader>
          {actionDialog && (() => {
            const items = actionDialog.order.items || [];
            const isPartial = actionDialog.selected.size > 0;
            const total = actionDialog.order.total || 0;
            // estimated impact
            let preview = total;
            if (isPartial) {
              const sub = (actionDialog.order.subtotal || 0) || 1;
              const lineTotal = Array.from(actionDialog.selected)
                .map((i) => computeLineTotal(items[i]))
                .reduce((a, b) => a + b, 0);
              const share = lineTotal / sub;
              const taxShare = (actionDialog.order.tax || 0) * share;
              const svcShare = (actionDialog.order.service_charge || 0) * share;
              preview = lineTotal + taxShare + svcShare;
            } else if (actionDialog.mode === 'refund' && actionDialog.amount) {
              preview = parseFloat(actionDialog.amount) || 0;
            }
            return (
              <div className="space-y-4 py-2">
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                  <strong>Note:</strong> Inventory is <em>not</em> restocked when an order is cancelled or refunded. This is intentional to prevent shrinkage abuse.
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select specific items for a <strong>partial {actionDialog.mode}</strong>, or leave all unchecked for the full order.
                  </p>
                  <div className="max-h-60 overflow-y-auto border border-border rounded-lg divide-y">
                    {items.map((it, idx) => {
                      const disabled = (actionDialog.mode === 'cancel' && it.status === 'cancelled')
                        || (actionDialog.mode === 'refund' && it.refunded);
                      return (
                        <label
                          key={idx}
                          className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface'}`}
                          data-testid={`action-item-${idx}`}
                        >
                          <input
                            type="checkbox"
                            checked={actionDialog.selected.has(idx)}
                            onChange={() => !disabled && toggleItem(idx)}
                            disabled={disabled}
                            className="w-4 h-4 accent-[var(--theme-accent)]"
                          />
                          <span className="flex-1">
                            <span className="font-medium">{it.quantity}× {it.menu_item_name}</span>
                            {disabled && <span className="ml-2 text-xs text-muted-foreground">({actionDialog.mode === 'cancel' ? 'already cancelled' : 'already refunded'})</span>}
                          </span>
                          <span className="text-muted-foreground">{sym}{computeLineTotal(it).toFixed(2)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {actionDialog.mode === 'refund' && !isPartial && (
                  <div>
                    <Label className="text-xs">Custom refund amount (leave blank for full)</Label>
                    <Input
                      type="number"
                      min="0" step="0.01"
                      value={actionDialog.amount}
                      onChange={(e) => setActionDialog({ ...actionDialog, amount: e.target.value })}
                      placeholder={`Full refund: ${sym}${total.toFixed(2)}`}
                      data-testid="refund-amount"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs">Reason</Label>
                  <Textarea
                    value={actionDialog.reason}
                    onChange={(e) => setActionDialog({ ...actionDialog, reason: e.target.value })}
                    placeholder={actionDialog.mode === 'cancel' ? 'e.g. customer no-show' : 'e.g. wrong item delivered'}
                    rows={2}
                    data-testid="action-reason"
                  />
                </div>

                <div className="flex items-center justify-between border-t pt-3 text-sm">
                  <span className="text-muted-foreground">
                    {actionDialog.mode === 'cancel' ? 'Lines to cancel:' : 'Refund amount:'}
                  </span>
                  <span className="font-bold text-lg">
                    {sym}{preview.toFixed(2)}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setActionDialog(null)}>Back</Button>
                  <Button
                    onClick={submitAction}
                    className={`flex-1 text-white ${actionDialog.mode === 'cancel' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                    data-testid="action-submit"
                  >
                    {actionDialog.mode === 'cancel'
                      ? (isPartial ? `Cancel ${actionDialog.selected.size} item(s)` : 'Cancel full order')
                      : (isPartial ? `Refund ${actionDialog.selected.size} item(s)` : 'Process refund')}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
